import { Select, MenuItem, Checkbox, ListItemText } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import { ThemePalette } from 'mui.theme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  options: any;
  selected: string | string[];
  setSelected: any;
  minWidth?: string;
  disabled?: boolean;
  multiple?: boolean;
  maxWidth?: string;
  getOptionLabel?: (option: string) => string;
}

const ProductionSelector = ({
  options,
  selected,
  setSelected,
  minWidth = '120px',
  disabled = false,
  multiple = false,
  maxWidth,
  getOptionLabel = (option: string) => option,
}: Props) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const handleChange = (event: any) => {
    const value = event.target.value;
    if (multiple && Array.isArray(value)) {
      if (value.length > 2) return;
      setSelected(value);
    } else {
      setSelected(value);
    }
  };

  const renderValue = (selectedVal: string | string[]) => {
    if (!multiple) {
      return getOptionLabel(selectedVal as string);
    }
    const arr = Array.isArray(selectedVal) ? selectedVal : [];
    if (arr.length === 0) {
      return 'None';
    }
    return arr.map(getOptionLabel).join(', ');
  };

  const renderMenuItem = (option: any) => {
    if (!multiple) {
      return (
        <MenuItem key={option} value={option} sx={{ fontSize: '12px' }}>
          {getOptionLabel(option)}
        </MenuItem>
      );
    }
    const selectedArr = Array.isArray(selected) ? selected : [];
    const isSelected = selectedArr.includes(option);
    const isDisabled = !isSelected && selectedArr.length >= 2;

    return (
      <MenuItem
        key={option}
        value={option}
        disabled={isDisabled}
        sx={{ fontSize: '12px' }}
      >
        <Checkbox
          checked={isSelected}
          size="small"
          sx={{
            padding: '0 5px 0 0',
            color: palette.textPrimary,
          }}
        />
        <ListItemText
          primary={getOptionLabel(option)}
          sx={{
            fontSize: '12px',
            margin: 0,
          }}
        />
      </MenuItem>
    );
  };

  return (
    <Select
      multiple={multiple}
      value={selected}
      onChange={handleChange}
      renderValue={renderValue}
      sx={{
        minWidth,
        maxWidth,
        height: '30px',
        fontSize: '11px',
        backgroundColor: enableDarkTheme
          ? palette.toolBarBackground
          : palette.menuOptions,
        color: palette.textPrimary,
        '& .MuiSelect-icon': {
          color: palette.textPrimary,
        },
        '& fieldset': {
          borderColor: enableDarkTheme ? palette.borderGrey : palette.border,
        },
        '&.MuiOutlinedInput-root': {
          '&:hover fieldset': {
            borderColor: palette.primary,
          },
          '&.Mui-focused fieldset': {
            borderColor: palette.primary,
          },
        },
      }}
      MenuProps={{
        PaperProps: {
          style: {
            backgroundColor: palette.menuOptions,
            color: enableDarkTheme
              ? palette.textSecondary
              : palette.textPrimary,
            fontSize: '12px',
          },
        },
      }}
      disabled={disabled}
    >
      {options.map(renderMenuItem)}
    </Select>
  );
};

export default ProductionSelector;
